import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../api';
import './PreviewFrameToolbar.css';

const IFRAME_LOAD_TIMEOUT_MS = 12000;
const DEFAULT_PREVIEW_PAGES = [{ id: 'store', path: 'index.html', kind: 'store', verified: true }];

function safePreviewPages(pages) {
  const allowed = new Map([
    ['store', { id: 'store', path: 'index.html', kind: 'store', label: 'החנות' }],
    ['admin', { id: 'admin', path: 'admin.html', kind: 'admin', label: 'ניהול החנות' }]
  ]);
  return (Array.isArray(pages) && pages.length ? pages : DEFAULT_PREVIEW_PAGES).flatMap(page => {
    const expected = allowed.get(page?.id);
    return expected && page.path === expected.path && page.verified === true ? [expected] : [];
  });
}

export function previewCopyButtonLabel(copied) {
  return copied ? '✓ הועתק!' : 'העתק קישור זמני';
}

export function previewPageUrl(previewUrl, pagePath, apiBaseUrl = API_URL) {
  if (!previewUrl) return null;
  // A managed app's owner link ends with #cv_owner=...: the page goes before the fragment.
  const [path, hash] = String(previewUrl).split('#');
  const base = new URL(path, apiBaseUrl).toString().replace(/\/+$/, '');
  const page = pagePath === 'index.html' ? `${base}/` : `${base}/${pagePath}`;
  return hash ? `${page}#${hash}` : page;
}

/** The link without the owner key (#cv_owner=...); any other fragment is kept. */
export function withoutOwnerKey(url) {
  if (!url) return url;
  const [path, hash = ''] = String(url).split('#');
  const rest = hash.split('&').filter(part => part && !part.startsWith('cv_owner=')).join('&');
  return rest ? `${path}#${rest}` : path;
}

export function previewDevicesForTarget(target, targetPlatforms = []) {
  if (target !== 'MOBILE') return [];
  const selected = new Set(Array.isArray(targetPlatforms) ? targetPlatforms : []);
  const devices = [];
  if (selected.has('ANDROID')) devices.push({ id: 'android', label: 'Android', frame: 'android' });
  if (selected.has('IOS')) devices.push({ id: 'ios', label: 'iPhone', frame: 'iphone' });
  return devices;
}

/**
 * Shows a compact inline preview and, on demand, moves the same verified preview
 * into an app-owned full-screen overlay. The overlay is intentionally local to
 * the client application: it never creates a second sandbox or extends a token.
 */
export default function PreviewFrame({ loading, previewUrl, pages, expiresAt, target = 'WEB', targetPlatforms = [], title = 'App preview', onClose, returnLabel = 'חזרה', onRefreshPreview }) {
  const [iframeState, setIframeState] = useState('loading');
  const [activePreviewUrl, setActivePreviewUrl] = useState(previewUrl);
  const [activePreviewBaseUrl, setActivePreviewBaseUrl] = useState(previewUrl);
  const [availablePages, setAvailablePages] = useState(() => safePreviewPages(pages));
  const [selectedPagePath, setSelectedPagePath] = useState('index.html');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [copyState, setCopyState] = useState('');
  const [copied, setCopied] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const devices = previewDevicesForTarget(target, targetPlatforms);
  const [selectedDevice, setSelectedDevice] = useState(() => devices[0]?.id || 'web');
  const timeoutRef = useRef(null);
  const copiedTimeoutRef = useRef(null);
  useEffect(() => () => clearTimeout(copiedTimeoutRef.current), []);

  useEffect(() => {
    if (!previewUrl) return undefined;
    const nextPages = safePreviewPages(pages);
    setAvailablePages(nextPages);
    setSelectedPagePath(current => nextPages.some(page => page.path === current) ? current : nextPages[0]?.path || 'index.html');
    setActivePreviewBaseUrl(previewUrl);
    setActivePreviewUrl(previewPageUrl(previewUrl, nextPages.find(page => page.path === selectedPagePath)?.path || nextPages[0]?.path || 'index.html'));
    setIframeState('loading');
    timeoutRef.current = setTimeout(() => setIframeState(previous => (previous === 'loading' ? 'error' : previous)), IFRAME_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timeoutRef.current);
  }, [previewUrl, pages]);

  useEffect(() => {
    if (!activePreviewBaseUrl) return;
    setActivePreviewUrl(previewPageUrl(activePreviewBaseUrl, selectedPagePath));
    setIframeState('loading');
  }, [selectedPagePath, activePreviewBaseUrl]);

  useEffect(() => {
    setSelectedDevice(current => devices.some(device => device.id === current) ? current : (devices[0]?.id || 'web'));
  }, [target, Array.isArray(targetPlatforms) ? targetPlatforms.join('|') : '']);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKeyDown = event => { if (event.key === 'Escape') setFullscreen(false); };
    document.addEventListener('keydown', onKeyDown);
    document.body.classList.add('preview-fullscreen-active');
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('preview-fullscreen-active');
    };
  }, [fullscreen]);

  const handleRetry = () => {
    setIframeState('loading');
    setLoadAttempt(previous => previous + 1);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIframeState(previous => (previous === 'loading' ? 'error' : previous)), IFRAME_LOAD_TIMEOUT_MS);
  };

  const handleCopy = async () => {
    if (!activePreviewUrl) return;
    try {
      // A copied link is for sharing: it never carries the owner key (#cv_owner=...), so whoever
      // receives it opens the app as a visitor, not as its owner.
      await navigator.clipboard.writeText(withoutOwnerKey(activePreviewUrl));
      setCopyState('הקישור הועתק');
      setCopied(true);
      clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopyState('לא ניתן להעתיק אוטומטית — העתק מהכתובת בדפדפן');
    }
  };

  const handleRefresh = async () => {
    if (!onRefreshPreview || refreshing) return;
    setRefreshing(true);
    try {
      const renewedUrl = await onRefreshPreview();
      const renewed = typeof renewedUrl === 'string' ? { previewUrl: renewedUrl } : renewedUrl;
      if (!renewed?.previewUrl) throw new Error('Preview session was not renewed');
      const nextPages = safePreviewPages(renewed.previewPages || availablePages);
      setAvailablePages(nextPages);
      setSelectedPagePath(current => nextPages.some(page => page.path === current) ? current : nextPages[0]?.path || 'index.html');
      setActivePreviewBaseUrl(renewed.previewUrl);
      setActivePreviewUrl(previewPageUrl(renewed.previewUrl, nextPages.find(page => page.path === selectedPagePath)?.path || nextPages[0]?.path || 'index.html'));
      setIframeState('loading');
      setLoadAttempt(previous => previous + 1);
    } catch {
      setIframeState('error');
    } finally {
      setRefreshing(false);
    }
  };

  if (!loading && !previewUrl) return null;
  const showSkeleton = loading || !previewUrl || iframeState === 'loading';
  const frame = (
    <div className={`preview-frame-wrap${fullscreen ? ' preview-frame-fullscreen' : ''}`} role={fullscreen ? 'dialog' : undefined} aria-modal={fullscreen || undefined} aria-label={fullscreen ? `${title} במסך מלא` : undefined}>
      {previewUrl && (
        <div className="preview-frame-toolbar">
          <div className="preview-frame-context">
            <span className="preview-frame-title">{title}</span>
            {availablePages.length > 1 && <div className="preview-page-tabs" role="tablist" aria-label="עמודי הפרויקט המאומתים">
              {availablePages.map(page => <button key={page.id} type="button" role="tab" aria-selected={selectedPagePath === page.path} className={selectedPagePath === page.path ? 'active' : ''} onClick={() => setSelectedPagePath(page.path)}>{page.label}</button>)}
            </div>}
          </div>
          <div className="preview-frame-actions" role="group" aria-label="פעולות תצוגה מקדימה">
            <button type="button" className="preview-control preview-frame-fullscreen-button" onClick={() => setFullscreen(value => !value)} aria-label={fullscreen ? 'חזרה מתצוגת מסך מלא' : 'הצג במסך מלא'}>
              {!fullscreen ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" /><path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" /></svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 3v3a2 2 0 0 1-2 2H4" /><path d="M21 8h-3a2 2 0 0 1-2-2V3" /><path d="M3 16h3a2 2 0 0 1 2 2v3" /><path d="M16 21v-3a2 2 0 0 1 2-2h3" /></svg>
              )}
              <span>{fullscreen ? 'הקטן' : 'מסך מלא'}</span>
            </button>
            <button type="button" className={`preview-control preview-frame-copy${copied ? ' is-copied' : ''}`} onClick={handleCopy} aria-label={previewCopyButtonLabel(copied)}>
              {!copied && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="1.5" /><path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" /></svg>}
              <span>{previewCopyButtonLabel(copied)}</span>
            </button>
            {onClose && <button type="button" className="preview-control preview-frame-close" onClick={onClose} aria-label={returnLabel}><span aria-hidden="true">←</span><span>{returnLabel}</span></button>}
          </div>
        </div>
      )}
      {previewUrl && (expiresAt || copyState) && <div className="preview-frame-meta" role="status">{expiresAt ? `הקישור הזמני פעיל עד ${new Date(expiresAt).toLocaleString()}` : ''}{expiresAt && copyState ? ' · ' : ''}{copyState}</div>}
      {previewUrl && devices.length > 0 && <div className="preview-device-controls" role="group" aria-label="גודל תצוגת מכשיר">
        <span>Web Preview מאומת</span>
        {devices.map(device => <button key={device.id} type="button" className={selectedDevice === device.id ? 'active' : ''} onClick={() => setSelectedDevice(device.id)}>{device.label}</button>)}
      </div>}
      {activePreviewUrl && (
        <div className={`preview-device-stage${selectedDevice !== 'web' ? ` preview-device-${selectedDevice}` : ''}`}>
          <iframe
            key={`${activePreviewUrl}-${loadAttempt}`}
            className="preview-frame-iframe"
            style={{ display: iframeState === 'error' ? 'none' : 'block' }}
            src={activePreviewUrl}
            title={title}
            onLoad={() => { clearTimeout(timeoutRef.current); setIframeState('loaded'); }}
            onError={() => { clearTimeout(timeoutRef.current); setIframeState('error'); }}
          />
        </div>
      )}
      {showSkeleton && <div className="preview-frame-skeleton" aria-hidden="true"><div className="pf-skel-bar pf-skel-bar-1" /><div className="pf-skel-bar pf-skel-bar-2" /><div className="pf-skel-block" /></div>}
      {iframeState === 'error' && previewUrl && (
        <div className="preview-frame-error" role="status">
          <span>The preview didn&rsquo;t load &mdash; the secure preview link may have expired.</span>
          <button type="button" className="submit-btn" onClick={handleRetry}>Retry</button>
          {onRefreshPreview && <button type="button" className="btn" onClick={handleRefresh} disabled={refreshing}>{refreshing ? 'Refreshing preview…' : 'Refresh preview'}</button>}
        </div>
      )}
    </div>
  );

  return fullscreen ? createPortal(frame, document.body) : frame;
}
